"use client";

import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import type {
  DietCatalog,
  DietCatalogCateringType,
} from "@/features/diets/types";
import { DietDateManager } from "@/lib/helpers/diet-dates";
import { useDiets } from "@/lib/hooks/use-diets";
import { useRescheduleUnavailableDates } from "../hooks/use-reschedule-unavailable-dates";
import type { CartItem } from "../types";
import { BuyMoreCta } from "./cart/buy-more-cta";
import { CartCateringGroup } from "./cart/cart-catering-group";
import { CartDiet } from "./cart/cart-diet";
import { CartHeaderButtons } from "./cart/cart-header-buttons";
import { DietSkeleton } from "./cart/diet-skeleton";
import { useCartStore } from "./cart-provider";
import { EmptyCart } from "./empty-cart";
import { usePendingRemovals } from "./pending-removals-provider";
import { RemovedItemNotice } from "./remove-from-cart/removed-item-notice";
import { CartRescheduleDialog } from "./reschedule/cart-reschedule-dialog";

type CartItemEnhanced = {
  diet: DietCatalog;
  item: CartItem;
  disabled: boolean;
};

export const CartList = () => {
  const cartStore = useCartStore((state) => state);
  const [cartItems, setCartItems] = useState<CartItemEnhanced[]>([]);
  const locale = useLocale();
  const t = useTranslations();
  const { cancel, getRemovalStatus } = usePendingRemovals();
  const { data, isPlaceholderData } = useDiets({
    filters: { diets: cartStore.getDietIds() },
    options: {
      placeholderData: (prev) => prev,
    },
    callback: cartStore.setDeliveryCosts,
  });
  const { closeDialog, isDialogOpen, reschedulingIds, rescheduledDiets } =
    useRescheduleUnavailableDates(
      isPlaceholderData ? undefined : data?.["hydra:member"],
    );

  useEffect(() => {
    if (!data) return;

    const disabledIds: string[] = [];
    const newCartItems =
      cartStore.items
        ?.map((item) => {
          const diet = data?.["hydra:member"].find(
            (d: DietCatalog) => d.id === item.configurationItem.dietId,
          );
          if (!diet) return null;

          const dietDateManager = new DietDateManager({
            diet,
            saturdays: true,
            sundays: true,
            locale,
          });
          const isDisabled = !dietDateManager.areDatesAvailable(
            item.configurationItem.dates.map((date) => new Date(date)),
          );
          if (isDisabled && item.configurationItem.id)
            disabledIds.push(item.configurationItem.id);
          return { diet, item, disabled: isDisabled };
        })
        .filter((item): item is CartItemEnhanced => item !== null) || [];

    setCartItems(newCartItems);
    cartStore.setDisabledItems(disabledIds);
  }, [cartStore.items, cartStore.setDisabledItems, data, locale]);

  const getDiet = (dietId: string) =>
    data?.["hydra:member"].find((d: DietCatalog) => d.id === dietId);

  // Cart positions grouped per catering (MPL-3757), keeping the order in which
  // each catering first appears in the cart.
  const cateringGroups = cartItems.reduce<
    { catering: DietCatalogCateringType; entries: CartItemEnhanced[] }[]
  >((groups, cartItem) => {
    const group = groups.find(
      (candidate) => candidate.catering.id === cartItem.diet.catering.id,
    );
    if (group) {
      group.entries.push(cartItem);
    } else {
      groups.push({ catering: cartItem.diet.catering, entries: [cartItem] });
    }
    return groups;
  }, []);

  // grid-cols-1 (minmax(0, 1fr)) instead of the implicit auto column: an
  // auto-sized track grows to the max-content width of its items, so a long
  // non-wrapping label (e.g. a truncated addon name) would stretch the cart and
  // scroll the whole page horizontally.
  return (
    <section className="relative grid h-full grid-cols-1 gap-4">
      <div className="h-full space-y-4 bg-background md:col-span-3 md:min-h-24">
        <CartHeaderButtons />
        {cartStore.items?.length === 0 &&
          cartStore.pendingItems?.length === 0 &&
          cartStore.pendingEditItems?.length === 0 && <EmptyCart />}
        {cartStore.pendingItems?.map((pendingItem) => {
          const diet = getDiet(pendingItem.dietId);
          if (!diet) return null;
          return <DietSkeleton key={pendingItem.dietId} />;
        })}
        {cateringGroups.map(({ catering, entries }) => (
          <CartCateringGroup catering={catering} key={catering.id}>
            {entries.map(({ diet, disabled, item }) => {
              if (
                cartStore.pendingEditItems?.some(
                  (pendingItem) => pendingItem.id === item.configurationItem.id,
                ) ||
                reschedulingIds.includes(item.configurationItem.id)
              )
                return <DietSkeleton key={diet.id} />;

              // The position is still in the cart while the undo window runs,
              // so its card is replaced instead of removed (MPL-3899).
              const removalStatus = getRemovalStatus(item.configurationItem.id);
              if (removalStatus !== "idle")
                return (
                  <RemovedItemNotice
                    className="min-h-85 rounded-lg border border-brand-grey2 bg-brand-white md:min-h-58"
                    key={item.configurationItem.id}
                    label={t("cart.undo.diet")}
                    onUndo={
                      removalStatus === "undo"
                        ? () => cancel(item.configurationItem.id)
                        : undefined
                    }
                  />
                );

              return (
                <CartDiet
                  diet={diet}
                  disabled={disabled}
                  item={item}
                  key={item.configurationItem.id}
                />
              );
            })}
          </CartCateringGroup>
        ))}

        <BuyMoreCta />
      </div>
      <CartRescheduleDialog
        items={rescheduledDiets}
        onClose={closeDialog}
        open={isDialogOpen}
      />
    </section>
  );
};
